import {
  Ruler,
  Droplets,
  AlertTriangle,
  Zap,
  Cog,
  Map,
  Mountain,
  Calculator,
  Gauge,
  Fan,
  Wind,
  FileText,
  MapPin,
  ClipboardList,
  Lightbulb,
  FileBarChart,
  Target,
  CheckCircle2,
  Leaf,
  Users,
  Sprout,
  Store,
  Wrench,
} from "lucide-react";

export const NAV_LINKS = [
  { label: "Home", id: "home" },
  { label: "About", id: "about" },
  { label: "Features", id: "features" },
  { label: "How It Works", id: "how-it-works" },
  { label: "Innovation", id: "innovation" },
  { label: "Contact", id: "contact" },
];

export const IMAGES = {
  heroAerial:
    "https://images.unsplash.com/photo-1558388556-2261d4cc1938?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MTN8MHwxfHNlYXJjaHwzfHxpbmRpYW4lMjBhZ3JpY3VsdHVyZSUyMGFlcmlhbHxlbnwwfHx8fDE3ODM3OTMzODN8MA&ixlib=rb-4.1.0&q=85",
  fieldRoad:
    "https://images.unsplash.com/photo-1630672140970-290903ff233c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MTN8MHwxfHNlYXJjaHwyfHxpbmRpYW4lMjBhZ3JpY3VsdHVyZSUyMGFlcmlhbHxlbnwwfHx8fDE3ODM3OTMzODN8MA&ixlib=rb-4.1.0&q=85",
  pipeInField:
    "https://images.unsplash.com/photo-1738598665806-7ecc32c3594c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA0MTJ8MHwxfHNlYXJjaHwzfHxhZ3JpY3VsdHVyZSUyMGlycmlnYXRpb24lMjBwaXBlcyUyMGZpZWxkfGVufDB8fHx8MTc4NDIyMjQ4Nnww&ixlib=rb-4.1.0&q=85",
  sprinkler:
    "https://images.unsplash.com/photo-1738598665698-7fd7af4b5e0c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA0MTJ8MHwxfHNlYXJjaHwyfHxhZ3JpY3VsdHVyZSUyMGlycmlnYXRpb24lMjBwaXBlcyUyMGZpZWxkfGVufDB8fHx8MTc4NDIyMjQ4Nnww&ixlib=rb-4.1.0&q=85",
  pivot:
    "https://images.unsplash.com/photo-1692369584496-3216a88f94c1?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA0MTJ8MHwxfHNlYXJjaHwxfHxhZ3JpY3VsdHVyZSUyMGlycmlnYXRpb24lMjBwaXBlcyUyMGZpZWxkfGVufDB8fHx8MTc4NDIyMjQ4Nnww&ixlib=rb-4.1.0&q=85",
  farmerCycle:
    "https://images.unsplash.com/photo-1738992765917-15d0b7d36683?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MDV8MHwxfHNlYXJjaHwzfHxpbmRpYW4lMjBmYXJtZXIlMjBzbWFydHBob25lJTIwZmllbGQlMjB0ZWNobm9sb2d5fGVufDB8fHx8MTc4NTk5OTY3Mnww&ixlib=rb-4.1.0&q=85",
  farmerSowing:
    "https://images.unsplash.com/photo-1696371269777-88d1ce71642c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MDV8MHwxfHNlYXJjaHwxfHxpbmRpYW4lMjBmYXJtZXIlMjBzbWFydHBob25lJTIwZmllbGQlMjB0ZWNobm9sb2d5fGVufDB8fHx8MTc4NTk5OTY3Mnww&ixlib=rb-4.1.0&q=85",
};

export const PROBLEMS = [
  {
    title: "Incorrect Pipe Selection",
    desc: "Pipe diameter and class are often chosen by habit rather than by the flow and pressure the line actually needs.",
    icon: Ruler,
  },
  {
    title: "Inadequate Water Delivery",
    desc: "Undersized or poorly routed pipelines can struggle to deliver the intended flow to the far end of the field.",
    icon: Droplets,
  },
  {
    title: "Excessive Pressure / Pipe Bursts",
    desc: "Without pressure analysis, lines can be exposed to surges and high static heads that lead to failures.",
    icon: AlertTriangle,
  },
  {
    title: "Higher Energy Consumption",
    desc: "Friction losses and mismatched components force pumps to work harder than the layout requires.",
    icon: Zap,
  },
  {
    title: "Unsuitable Pump Selection",
    desc: "Pumps chosen without a head and flow calculation may be over- or under-specified for the pipeline.",
    icon: Cog,
  },
];

export const FEATURES = [
  {
    title: "Pipeline Route Mapping",
    desc: "Plot the start point, endpoint and full route of the pipeline directly on a digital map.",
    icon: Map,
  },
  {
    title: "Elevation Analysis",
    desc: "Read ground levels and elevation changes along the entire route to understand the terrain.",
    icon: Mountain,
  },
  {
    title: "Hydraulic Calculations",
    desc: "Compute head, friction loss, minor losses, velocity and pressure requirements for the line.",
    icon: Calculator,
  },
  {
    title: "Pipe Pressure Class Recommendation",
    desc: "Get an engineering-based pipe pressure class recommendation based on the calculated pressure conditions along the route.",
    icon: Gauge,
  },
  {
    title: "Pump Requirement Recommendation",
    desc: "Estimate suitable pump head and capacity requirements based on the pipeline's hydraulic calculations.",
    icon: Fan,
  },
  {
    title: "Air Valve & NRV Recommendations",
    desc: "Identify suitable locations for air valves and non-return valves along the pipeline.",
    icon: Wind,
  },
  {
    title: "Detailed Pipeline Design Report",
    desc: "Generate a structured report with route information, calculations, graphs and recommendations.",
    icon: FileText,
  },
];

export const STEPS = [
  {
    no: "01",
    title: "Map Pipeline Route",
    desc: "Select the starting point, endpoint and pipeline route on a digital map.",
    icon: MapPin,
  },
  {
    no: "02",
    title: "Enter Pipeline Requirements",
    desc: "Enter flow requirement, pipe diameter, sump/well depth and required parameters.",
    icon: ClipboardList,
  },
  {
    no: "03",
    title: "Analyze Elevation",
    desc: "Analyze elevation and ground levels along the pipeline route.",
    icon: Mountain,
  },
  {
    no: "04",
    title: "Perform Hydraulic Calculations",
    desc: "Calculate head, friction loss, minor losses, velocity and pressure requirements.",
    icon: Calculator,
  },
  {
    no: "05",
    title: "Get Smart Recommendations",
    desc: "Recommend appropriate pipe pressure class, pump requirement, air valves and NRVs.",
    icon: Lightbulb,
  },
  {
    no: "06",
    title: "Generate Design Report",
    desc: "Generate a detailed pipeline design report with route information, calculations, graphs and recommendations.",
    icon: FileBarChart,
  },
];

export const IMPACTS = [
  {
    title: "Improve Pipeline Design Accuracy",
    desc: "Replace guesswork with structured, calculation-backed design decisions for every route.",
    icon: Target,
  },
  {
    title: "Support Appropriate Pipe & Pump Selection",
    desc: "Base pipe class and pump choices on the pipeline's real hydraulic requirements.",
    icon: CheckCircle2,
  },
  {
    title: "Reduce Avoidable Water & Energy Losses",
    desc: "Design lines that account for friction, elevation and pressure to avoid unnecessary losses.",
    icon: Leaf,
  },
  {
    title: "Make Engineering-Based Planning Accessible",
    desc: "Bring a structured engineering workflow within reach of farmers, dealers and technicians.",
    icon: Users,
  },
];

export const AUDIENCE = [
  {
    title: "Farmers",
    desc: "Plan on-farm pipelines with a clearer, calculation-based understanding of pipe and pump needs.",
    icon: Sprout,
    image:
      "https://static.prod-images.emergentagent.com/jobs/b35aaf5e-e0cb-45c3-bc89-781ffa328bb4/images/8d739dde79fb3307b37af49751b04e4b1c69bdd690edbf9ea3e04fe9f4b63c47.jpeg",
  },
  {
    title: "Pipe & Pump Dealers",
    desc: "Support customers with structured recommendations for pipe class, diameter and pump selection.",
    icon: Store,
    image:
      "https://static.prod-images.emergentagent.com/jobs/b35aaf5e-e0cb-45c3-bc89-781ffa328bb4/images/45fad9aca13b52b8deeec1d5f55cc53610b823315a5b9c52379363e2b4aefb35.jpeg",
  },
  {
    title: "Pipeline Technicians",
    desc: "Design and document pipelines with elevation data, hydraulic calculations and reports.",
    icon: Wrench,
    image:
      "https://static.prod-images.emergentagent.com/jobs/b35aaf5e-e0cb-45c3-bc89-781ffa328bb4/images/5f891625f4f403da20f76d065a83c97bf6f881eb9aec587931e3cd0c78390b1d.jpeg",
  },
];

export const COMPANY = {
  name: "PipeMitra Technologies Private Limited",
  email: "info@pipemitra.com",
  website: "pipemitra.com",
  location: "Maharashtra, India",
};
